import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, ClickAwayListener, InputBase, Popper, Typography } from '@mui/material';
import { Close, KeyboardArrowDown } from '@mui/icons-material';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

// COGS (post-GST total, INR) range filter for the Simple/Bundle view. Structure, tokens and the
// draft/apply model are cloned from DocRangeFilter so the two chips read identically in the
// toolbar. Min/Max are rupee amounts; "No cost data" selects only bundles with no COGS total
// (mutually exclusive with min/max/partial); "Partial only" is an additional constraint that
// combines with a range (bundles whose total is present but partial === true).

export interface CogsRangeValue {
    min: string;
    max: string;
    none: boolean;
    partialOnly: boolean;
}

export const EMPTY_COGS_RANGE: CogsRangeValue = { min: '', max: '', none: false, partialOnly: false };

export const isCogsRangeActive = (v: CogsRangeValue) => v.none || v.partialOnly || v.min !== '' || v.max !== '';

// Query params for the API. "No cost data" (none) is mutually exclusive — it emits only cogsNone
// and nothing else. Otherwise emit cogsMin/cogsMax when set and cogsPartialOnly when the Partial
// toggle is on.
export const cogsRangeParams = (v: CogsRangeValue): Record<string, string> => {
    if (v.none) return { cogsNone: 'true' };
    if (!isCogsRangeActive(v)) return {};
    const out: Record<string, string> = {};
    if (v.min !== '') out.cogsMin = v.min;
    if (v.max !== '') out.cogsMax = v.max;
    if (v.partialOnly) out.cogsPartialOnly = 'true';
    return out;
};

const PRESETS: { label: string; hint: string; min: string; max: string }[] = [
    { label: 'Low', hint: '≤ ₹100', min: '', max: '100' },
    { label: 'Mid', hint: '₹100–₹300', min: '100', max: '300' },
    { label: 'High', hint: '₹300+', min: '300', max: '' },
];

const describe = (v: CogsRangeValue): string => {
    if (v.none) return 'No cost data';
    let range = '';
    if (v.min !== '' && v.max !== '') range = v.min === v.max ? `₹${v.min}` : `₹${v.min} – ₹${v.max}`;
    else if (v.min !== '') range = `≥ ₹${v.min}`;
    else if (v.max !== '') range = `≤ ₹${v.max}`;
    if (v.partialOnly) return range ? `${range} · Partial` : 'Partial';
    return range || 'All';
};

interface CogsRangeFilterProps {
    value: CogsRangeValue;
    onChange: (next: CogsRangeValue) => void;
    minWidth?: number;
}

const CogsRangeFilter: React.FC<CogsRangeFilterProps> = ({ value, onChange, minWidth = 170 }) => {
    const reduceMotion = useReducedMotion();
    const anchorRef = useRef<HTMLDivElement>(null);
    const minRef = useRef<HTMLInputElement>(null);
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState<CogsRangeValue>(value);
    const active = isCogsRangeActive(value);

    useEffect(() => {
        if (open) {
            setDraft(value);
            const t = setTimeout(() => minRef.current?.focus(), 60);
            return () => clearTimeout(t);
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const invalid = !draft.none && draft.min !== '' && draft.max !== '' && Number(draft.min) > Number(draft.max);
    const summary = useMemo(() => {
        if (invalid) return 'Min can’t be higher than Max';
        if (draft.none) return 'Only bundles with no COGS cost data';
        const parts: string[] = [];
        if (draft.min !== '' && draft.max !== '') parts.push(draft.min === draft.max ? `COGS of exactly ₹${draft.min}` : `COGS from ₹${draft.min} to ₹${draft.max}`);
        else if (draft.min !== '') parts.push(`COGS of ₹${draft.min} or more`);
        else if (draft.max !== '') parts.push(`COGS of ₹${draft.max} or less`);
        if (draft.partialOnly) parts.push('partially-costed only');
        if (parts.length === 0) return 'Showing every COGS — set a Min and/or Max';
        return parts.join(', ');
    }, [draft, invalid]);

    const apply = (next: CogsRangeValue) => {
        onChange(next);
        setOpen(false);
    };
    const setNum = (key: 'min' | 'max', raw: string) => {
        const cleaned = raw.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
        setDraft(d => ({ ...d, [key]: cleaned, none: false }));
    };
    const presetActive = (p: typeof PRESETS[number]) => !draft.none && draft.min === p.min && draft.max === p.max;

    const field = (key: 'min' | 'max', label: string) => (
        <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8A8078', mb: 0.4 }}>{label}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.25, height: 38, borderRadius: '10px', border: '1px solid', borderColor: invalid ? '#dc2626' : '#ECECE4', bgcolor: '#fff', transition: 'border-color 150ms, box-shadow 150ms', '&:focus-within': { borderColor: invalid ? '#dc2626' : '#DC5829', boxShadow: `0 0 0 3px ${invalid ? 'rgba(220,38,38,0.12)' : 'rgba(220,88,41,0.12)'}` } }}>
                <Typography sx={{ fontSize: '0.85rem', color: '#8A8078', fontWeight: 700 }}>₹</Typography>
                <InputBase
                    inputRef={key === 'min' ? minRef : undefined}
                    value={draft[key]}
                    onChange={(e) => setNum(key, e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !invalid) apply(draft); }}
                    placeholder={key === 'min' ? 'e.g. 100' : 'e.g. 500'}
                    inputProps={{ inputMode: 'decimal', 'aria-label': `${label} COGS in rupees` }}
                    sx={{ flex: 1, fontSize: '0.9rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                />
            </Box>
        </Box>
    );

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Box sx={{ position: 'relative', minWidth }}>
                <Box
                    ref={anchorRef}
                    onClick={() => setOpen(o => !o)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); } else if (e.key === 'Escape') setOpen(false); }}
                    tabIndex={0}
                    role="button"
                    aria-haspopup="dialog"
                    aria-expanded={open}
                    sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, height: 36, px: 1.25,
                        borderRadius: '10px', bgcolor: '#fff', border: '1px solid',
                        borderColor: open || active ? 'primary.main' : '#ECECE4',
                        boxShadow: open ? '0 0 0 3px rgba(220, 88, 41, 0.12)' : 'none',
                        cursor: 'pointer', outline: 'none', transition: 'border-color 150ms ease, box-shadow 150ms ease',
                        '&:hover': { borderColor: '#DC5829' },
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, minWidth: 0 }}>
                        <Typography component="span" sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#8A8078', textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0 }}>
                            COGS
                        </Typography>
                        <Typography noWrap sx={{ fontSize: '0.82rem', fontWeight: active ? 700 : 500, color: active ? '#1A1410' : 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                            {describe(value)}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                        {active && (
                            <Box
                                component="span"
                                role="button"
                                aria-label="Clear COGS filter"
                                onClick={(e) => { e.stopPropagation(); onChange(EMPTY_COGS_RANGE); }}
                                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: '50%', color: 'text.secondary', '&:hover': { bgcolor: '#F3F2EC', color: '#DC5829' } }}
                            >
                                <Close sx={{ fontSize: 14 }} />
                            </Box>
                        )}
                        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.18, ease: 'easeOut' }} style={{ display: 'flex' }}>
                            <KeyboardArrowDown sx={{ fontSize: 20, color: 'text.secondary' }} />
                        </motion.div>
                    </Box>
                </Box>

                <Popper
                    open={open}
                    anchorEl={anchorRef.current}
                    placement="bottom-start"
                    sx={{ zIndex: 1400 }}
                    modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
                >
                    <>
                        <AnimatePresence>
                            {open && (
                                <motion.div
                                    role="dialog"
                                    aria-label="Filter by COGS range"
                                    initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.97 }}
                                    animate={reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
                                    exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -4, scale: 0.98 }}
                                    transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
                                    style={{ transformOrigin: 'top left' }}
                                >
                                    <Box sx={{ width: 320, p: 1.75, borderRadius: '14px', bgcolor: '#fff', border: '1px solid #ECECE4', boxShadow: '0 18px 44px rgba(26, 20, 15, 0.18)' }}>
                                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, mb: 1.25 }}>COGS (post-GST)</Typography>

                                        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                                            {field('min', 'Min')}
                                            <Typography sx={{ pb: 1, color: '#8A8078', fontWeight: 700 }}>–</Typography>
                                            {field('max', 'Max')}
                                        </Box>

                                        <Box sx={{ minHeight: 20, mt: 0.9 }} aria-live="polite">
                                            <AnimatePresence mode="wait" initial={false}>
                                                <motion.div
                                                    key={summary}
                                                    initial={reduceMotion ? false : { opacity: 0, y: 3 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0 }}
                                                    transition={{ duration: 0.12 }}
                                                >
                                                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: invalid ? '#dc2626' : 'text.secondary' }}>{summary}</Typography>
                                                </motion.div>
                                            </AnimatePresence>
                                        </Box>

                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.25 }}>
                                            {PRESETS.map(p => {
                                                const sel = presetActive(p);
                                                return (
                                                    <Box
                                                        key={p.label}
                                                        component="button"
                                                        type="button"
                                                        onClick={() => setDraft(d => ({ ...d, min: p.min, max: p.max, none: false }))}
                                                        sx={{ px: 1.1, height: 26, borderRadius: '999px', border: '1px solid', borderColor: sel ? '#DC5829' : '#DAD6CC', bgcolor: sel ? 'rgba(220, 88, 41, 0.1)' : '#fff', color: sel ? '#B8451E' : 'text.secondary', font: 'inherit', fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer', transition: 'all 140ms', '&:hover': { borderColor: '#DC5829' }, '&:focus-visible': { outline: '2px solid #DC5829', outlineOffset: 1 } }}
                                                    >
                                                        {p.label} <Box component="span" sx={{ opacity: 0.65, fontWeight: 600 }}>{p.hint}</Box>
                                                    </Box>
                                                );
                                            })}
                                            <Box
                                                component="button"
                                                type="button"
                                                onClick={() => setDraft(d => ({ ...d, partialOnly: !d.partialOnly, none: false }))}
                                                sx={{ px: 1.1, height: 26, borderRadius: '999px', border: '1px solid', borderColor: draft.partialOnly ? '#DC5829' : '#DAD6CC', bgcolor: draft.partialOnly ? 'rgba(220, 88, 41, 0.1)' : '#fff', color: draft.partialOnly ? '#B8451E' : 'text.secondary', font: 'inherit', fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer', '&:hover': { borderColor: '#DC5829' }, '&:focus-visible': { outline: '2px solid #DC5829', outlineOffset: 1 } }}
                                            >
                                                Partial only
                                            </Box>
                                            <Box
                                                component="button"
                                                type="button"
                                                onClick={() => setDraft(d => ({ ...d, min: '', max: '', partialOnly: false, none: !d.none }))}
                                                sx={{ px: 1.1, height: 26, borderRadius: '999px', border: '1px solid', borderColor: draft.none ? '#DC5829' : '#DAD6CC', bgcolor: draft.none ? 'rgba(220, 88, 41, 0.1)' : '#fff', color: draft.none ? '#B8451E' : 'text.secondary', font: 'inherit', fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer', '&:hover': { borderColor: '#DC5829' }, '&:focus-visible': { outline: '2px solid #DC5829', outlineOffset: 1 } }}
                                            >
                                                No cost data
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.75 }}>
                                            <Button size="small" onClick={() => apply(EMPTY_COGS_RANGE)} sx={{ textTransform: 'none', fontWeight: 700, color: 'text.secondary', '&:hover': { color: '#DC5829' } }}>
                                                Clear
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                disableElevation
                                                disabled={invalid}
                                                onClick={() => apply(draft)}
                                                sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '999px', px: 2.25, bgcolor: '#DC5829', '&:hover': { bgcolor: '#B8451E' } }}
                                            >
                                                Apply
                                            </Button>
                                        </Box>
                                    </Box>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </>
                </Popper>
            </Box>
        </ClickAwayListener>
    );
};

export default CogsRangeFilter;
